const React = require("react");
const { Link } = require("gatsby");

const UNSAFE_JS_STRIP_RE = /[\p{Cc}\p{Cf}\p{Zs}]+/gu;
const JAVASCRIPT_PREFIX = "javascript:";

const stripUnsafeChars = (value) => value.replaceAll(UNSAFE_JS_STRIP_RE, "");

const isSafeJavascriptCode = (value) => {
  const normalized = stripUnsafeChars(value);
  if (!normalized) return false;
  return (
    normalized === ";" ||
    normalized === "void(0)" ||
    normalized === "void(0);" ||
    normalized === "void0" ||
    normalized === "void0;"
  );
};

const hasUnsafeJavascriptProtocol = (value) => {
  if (!value) return false;
  const normalized = stripUnsafeChars(String(value).toLowerCase());
  if (!normalized.startsWith(JAVASCRIPT_PREFIX)) return false;
  return !isSafeJavascriptCode(normalized.slice(JAVASCRIPT_PREFIX.length));
};

const normalizeHref = (href) => {
  if (!href) return "";
  if (typeof href === "string") return href.trim();
  return String(href).trim();
};

const isUnsafeJavascriptHref = (href, lowerHref) => {
  if (!href) return false;
  const lower = lowerHref || String(href).toLowerCase();

  const collapsed = stripUnsafeChars(lower);
  if (hasUnsafeJavascriptProtocol(collapsed)) {
    return true;
  }
  if (!collapsed.includes("%")) return false;

  try {
    let decoded = lower;
    for (let i = 0; i < 3 && decoded.includes("%"); i += 1) {
      decoded = decodeURIComponent(decoded);
      if (hasUnsafeJavascriptProtocol(decoded)) {
        return true;
      }
    }
    return false;
  } catch {
    return true;
  }
};

const sanitizeHref = (href) => {
  const normalized = normalizeHref(href);
  if (!normalized) {
    return { normalized: "", lowerHref: "", isUnsafe: false };
  }

  const lowerHref = normalized.toLowerCase();
  const isUnsafe = isUnsafeJavascriptHref(normalized, lowerHref);

  if (isUnsafe) {
    return { normalized: "", lowerHref: "", isUnsafe: true };
  }

  return { normalized, lowerHref, isUnsafe: false };
};

const isExternalHref = (href, lowerHref, alreadyNormalized = false) => {
  const normalized = alreadyNormalized ? String(href || "") : normalizeHref(href);
  const lower = lowerHref || normalized.toLowerCase();
  if (!normalized) return false;

  if (
    (!alreadyNormalized && isUnsafeJavascriptHref(normalized, lower)) ||
    lower.startsWith("#") ||
    lower.startsWith("/") ||
    lower.startsWith("mailto:") ||
    lower.startsWith("tel:")
  ) {
    return false;
  }

  return /^https?:\/\//.test(normalized) || lower.startsWith("//");
};

const ensureRel = (rel) => {
  const tokens = String(rel || "")
    .split(/\s+/)
    .map((token) => token.trim())
    .filter(Boolean);

  const seen = new Set(tokens.map((token) => token.toLowerCase()));
  if (!seen.has("noopener")) {
    tokens.push("noopener");
    seen.add("noopener");
  }
  if (!seen.has("noreferrer")) {
    tokens.push("noreferrer");
  }

  return tokens.join(" ");
};

const Anchor = React.forwardRef(function Anchor(props, ref) {
  const { href, target, rel, children, ...rest } = props || {};
  const { normalized, lowerHref, isUnsafe } = sanitizeHref(href);

  if (isUnsafe) {
    return React.createElement(
      "a",
      { ...rest, href: undefined, "aria-disabled": true, role: "link", ref },
      children
    );
  }

  // External links -> real <a> + sécurité
  if (isExternalHref(normalized, lowerHref, true)) {
    const safeTarget = target === "_blank" ? target : "_blank";
    const safeRel = ensureRel(rel);

    return React.createElement(
      "a",
      {
        ...rest,
        href: normalized,
        target: safeTarget,
        rel: safeRel,
        ref,
      },
      children
    );
  }

  // Ancres, mailto, tel... restent des <a> classiques
  if (
    !normalized ||
    lowerHref.startsWith("#") ||
    lowerHref.startsWith("mailto:") ||
    lowerHref.startsWith("tel:")
  ) {
    return React.createElement("a", {
      ...rest,
      href: normalized || undefined,
      target,
      rel,
      ref,
    });
  }

  // Liens internes (pages du site) -> Gatsby <Link> pour éviter le full reload
  if (normalized.startsWith("/")) {
    return React.createElement(
      Link,
      { ...rest, to: normalized, ref },
      children
    );
  }

  // Fallback : lien standard
  return React.createElement("a", { ...rest, href, target, rel, ref });
});

// Lazy-load to avoid pulling components during SSR unless needed
let FAQ, FAQItem;
let LinkCard, LinkGrid;
let Restaurants;
let LocalFlavors;
let ExcursionsGrid;
let MapChip;
let InfoGrid, InfoCard;
try {
  ({ FAQ, FAQItem } = require("../components/FAQ/FAQ.jsx"));
} catch {}
try {
  LinkCard = require("../components/LinkCard/LinkCard.jsx").default;
  LinkGrid = require("../components/LinkCard/LinkGrid.jsx").default;
} catch {}
try {
  Restaurants = require("../components/Restaurants/Index.jsx").default;
} catch {}
try {
  LocalFlavors = require("../components/LocalFlavors/Index.jsx").default;
} catch {}
try {
  ExcursionsGrid = require("../components/ExcursionsGrid/Index.jsx");
} catch {}
try {
  MapChip = require("../components/MapChip/Index.jsx").default;
} catch {}
try {
  ({ InfoGrid, InfoCard } = require("../components/InfoCards/InfoCards.jsx"));
} catch {}

const mdxComponents = {
  a: Anchor,
  ...(FAQ ? { FAQ } : {}),
  ...(FAQItem ? { FAQItem } : {}),
  ...(LinkCard ? { LinkCard } : {}),
  ...(LinkGrid ? { LinkGrid } : {}),
  ...(Restaurants ? { Restaurants } : {}),
  ...(LocalFlavors ? { LocalFlavors } : {}),
  ...(ExcursionsGrid ? { ExcursionsGrid } : {}),
  ...(MapChip ? { MapChip } : {}),
  ...(InfoGrid ? { InfoGrid } : {}),
  ...(InfoCard ? { InfoCard } : {}),
};

module.exports = {
  mdxComponents,
};
